import type { BookingResponse } from '@models/landing/api/api-response.model';
import type { ContactType } from '@models/landing/master-data/master-data.model';

export interface PassengerRestrictions {
  allowedBoardingPass: boolean;
  allowedEmail: boolean;
  allowedSMS: boolean;
  allowedWAPP: boolean;
}

export type TranslationFn = (key: string) => string;

export enum PassengerTypeEnum {
  Adult = 'adult',
  Child = 'child',
  Infant = 'infant',
}

/**
 * Passenger type codes from booking data
 * Maps numeric codes to passenger types
 */
// Constants to avoid magic numbers
const ADULT_CODE = 1;
const CHILD_CODE = 6;
const INFANT_CODE = 5;

export enum PassengerTypeCode {
  Adult = ADULT_CODE,
  Child = CHILD_CODE,
  Infant = INFANT_CODE,
}

/**
 * Service Request (SSR) types for ancillary services
 */
export enum SsrType {
  SEAT = 'SEAT',
  HANDBAGGAGE = 'HANDBAGGAGE',
  CHECKEDBAGGAGE = 'BAGGAGE',
  MEAL = 'MEAL',
  IFE = 'IFE',
  LOUNGE = 'LOUNGE',
  ASSIST = 'ASSIST',
}

/**
 * Check-in status for passengers
 */
export enum CheckInStatus {
  NOT_CHECKED_IN = 'Not checked in',
  INCOMPLETE = 'incomplete',
  CHECKED_IN = 'checked-in',
}

/**
 * Status string values from API
 */
export enum StatusType {
  CHECKED_IN = 'CheckedIn',
  NOT_CHECKED_IN = 'NotCheckedIn',
}

export interface CheckedInPassengerListProps {
  bookingResponse?: BookingResponse;
}

export interface ContactInfo {
  contactType: ContactType;
  contactField?: string | null;
  contactPhone?: string | null;
  countryCode?: string | null;
}

export interface ContactDataType {
  formState: {
    countryCode: string;
    email: string;
    phone: string;
  };
  passenger: {
    name: string;
    type: PassengerTypeEnum;
    typeLabel: string;
  };
}

export enum FarebrandTypeEnum {
  Lite = 'lite',
  Value = 'value',
  Flex = 'flex',
  EconomyClass = 'economy class',
  BusinessClass = 'business class',
  Business = 'business',
  Economy = 'economy',
  FlyPlusVisa = 'fly + visa',
  LiteTcase = 'Lite',
  ValueTcase = 'Value',
  FlexTcase = 'Flex',
  EconomyClassTcase = 'Economy Class',
  BusinessClassTcase = 'Business Class',
  BusinessTcase = 'Business',
  EconomyTcase = 'Economy',
  FlyPlusVisaTcase = 'Fly + Visa',
}

export interface SeatAssignment {
  /** Route label, e.g. "AMM to DXB" */
  route: string;
  /** Seat identifier, e.g. "12 B" — null means not assigned (NA) */
  seatNumber: string | null;
  /** Optional i18n key when a null seat should render as a translated value (e.g. Not selected). */
  valueKey?: string;
  /** Optional operating carrier code for segment-level rendering decisions upstream. */
  flightInfo?: string;
  operatingCarrier?: string;
}

export interface Passenger {
  id: string;
  name: string;
  type: 'adult' | 'child' | 'infant';
  flightClass: string;
  seats: SeatAssignment[];
  handBaggageAllowance: string;
  checkedBaggageAllowance: string;
  checkInStatus: CheckInStatus;
}

export interface PassengersTableProps {
  passengers: Passenger[];
}
